import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type DigitalWalletTransaction =
  | vp.GooglePayChargeTransactionResponse
  | vp.PayPalWithdrawTransactionResponse
  | vp.VenmoWithdrawTransactionResponse;

type DigitalWalletTransactionInfoProps = {
  transaction: DigitalWalletTransaction;
  transactionType: 'GooglePay' | 'PayPal' | 'Venmo';
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<DigitalWalletTransactionInfoProps>;

const DigitalWalletTransactionInfo: FC = (props: DigitalWalletTransactionInfoProps) => {
  const { transaction, transactionType } = props;
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const { state } = useLocation<DataProps>();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.DigitalWalletTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  // Helper function to map transaction type to wallet display name
  const getWalletDisplayName = (type: 'GooglePay' | 'PayPal' | 'Venmo'): string => {
    const walletTypeMap: Record<typeof type, string> = {
      GooglePay: 'Google Pay',
      PayPal: 'PayPal',
      Venmo: 'Venmo',
    };
    return walletTypeMap[type];
  };

  // Helper function to get wallet-specific token field
  const getWalletToken = () => {
    if (transactionType === 'GooglePay' && 'GooglePayToken' in transaction) {
      return transaction.GooglePayToken;
    } else if (transactionType === 'PayPal' && 'PayPalToken' in transaction) {
      return transaction.PayPalToken;
    } else if (transactionType === 'Venmo' && 'VenmoToken' in transaction) {
      return transaction.VenmoToken;
    }
    return '';
  };

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table
        name="transaction-details"
        variant="pair"
        columns={{
          detailKey: { header: t('TransactionDetails.header') || '' },
          detailValue: { header: '' },
        }}
        rows={[
          {
            detailKey: t('TransactionDetails.transactionID'),
            detailValue: transaction.TransactionID,
          },
          {
            detailKey: t('TransactionDetails.transactionStatus'),
            detailValue: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          ...(transaction.TransactionDateTime
            ? [
                {
                  detailKey: t('TransactionDetails.transactionDateTime'),
                  detailValue: moment(transaction.TransactionDateTime).format('YYYY-MM-DD HH:mm:ss'),
                },
              ]
            : []),

          ...(transaction.Amount
            ? [
                {
                  detailKey: t('TransactionDetails.amount'),
                  detailValue: `$${formattedCurrency(transaction.Amount)}`,
                },
              ]
            : []),
          ...(transaction.Currency
            ? [
                {
                  detailKey: t('TransactionDetails.currency'),
                  detailValue: transaction.Currency,
                },
              ]
            : []),
          ...(transaction.HoldAmount
            ? [
                {
                  detailKey: t('TransactionDetails.holdAmount'),
                  detailValue: `$${formattedCurrency(transaction.HoldAmount)}`,
                },
              ]
            : []),
          ...(transaction.ClientReferenceNumber
            ? [
                {
                  detailKey: t('TransactionDetails.clientReferenceNumber'),
                  detailValue: transaction.ClientReferenceNumber,
                },
              ]
            : []),
          ...(transaction.Notes
            ? [
                {
                  detailKey: t('TransactionDetails.notes'),
                  detailValue: transaction.Notes,
                },
              ]
            : []),
          ...(transaction.LastModified
            ? [
                {
                  detailKey: t('TransactionDetails.lastModified'),
                  detailValue: moment(transaction.LastModified).format('YYYY-MM-DD HH:mm:ss'),
                },
              ]
            : []),
        ]}
      />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('walletDetailsTitle', { walletType: getWalletDisplayName(transactionType) }) || ''} />
      </Box>
      <Table
        name="wallet-details"
        variant="pair"
        columns={{
          detailKey: { header: t('WalletDetails.header', { walletType: getWalletDisplayName(transactionType) }) || '' },
          detailValue: { header: '' },
        }}
        rows={[
          ...(transaction.ClientAccountID
            ? [
                {
                  detailKey: clientAccountsEnabled ? t('WalletDetails.clientAccountID') : t('WalletDetails.contactId'),
                  detailValue: transaction.ClientAccountID,
                },
              ]
            : []),
          ...(getWalletToken()
            ? [
                {
                  detailKey: t('WalletDetails.token'),
                  detailValue: getWalletToken(),
                },
              ]
            : []),
        ]}
      />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table
            name="error-info"
            variant="pair"
            columns={{
              detailKey: { header: t('ErrorInfo.header') || '' },
              detailValue: { header: '' },
            }}
            rows={[
              ...(state?.failureReason
                ? [
                    {
                      detailKey: t('ErrorInfo.failureReason'),
                      detailValue: state.failureReason,
                    },
                  ]
                : []),
              ...(transaction.ErrorMessage
                ? [
                    {
                      detailKey: t('ErrorInfo.errorMessage'),
                      detailValue: transaction.ErrorMessage,
                    },
                  ]
                : []),
            ]}
          />
        </>
      )}
    </div>
  );
};

export default DigitalWalletTransactionInfo;
